import { Plus, X } from 'lucide-react';
import {
  FieldArrayWithId,
  UseFieldArrayAppend,
  UseFieldArrayRemove,
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { BenefitType } from '@/services/types/coverage-types';

import { CoverageFormData } from '../types';

interface ClaimsSectionProps {
  register: UseFormRegister<CoverageFormData>;
  setValue: UseFormSetValue<CoverageFormData>;
  watch: UseFormWatch<CoverageFormData>;
  claimFields: FieldArrayWithId<CoverageFormData, 'claims', 'id'>[];
  appendClaim: UseFieldArrayAppend<CoverageFormData, 'claims'>;
  removeClaim: UseFieldArrayRemove;
  onRemoveClaim?: (index: number) => void;
  removingClaimIndex?: number | null;
}

export const ClaimsSection = ({
  register,
  setValue,
  watch,
  claimFields,
  appendClaim,
  removeClaim,
  onRemoveClaim,
  removingClaimIndex,
}: ClaimsSectionProps) => {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">Claims</CardTitle>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() =>
              appendClaim({ benefit_type: null, claim_identifier: null })
            }
          >
            <Plus className="h-4 w-4 mr-1" />
            Add Claim
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {claimFields.map((field, index) => (
          <div key={field.id} className="border rounded-lg p-4 space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium">Claim {index + 1}</h4>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() =>
                  onRemoveClaim ? onRemoveClaim(index) : removeClaim(index)
                }
                disabled={removingClaimIndex === index}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor={`claims.${index}.claim_identifier`}>
                  Claim Number
                </Label>
                <Input
                  id={`claims.${index}.claim_identifier`}
                  placeholder="e.g., CLM-123456"
                  {...register(`claims.${index}.claim_identifier`)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor={`claims.${index}.benefit_type`}>
                  Benefit Type
                </Label>
                <Select
                  value={watch(`claims.${index}.benefit_type`) || undefined}
                  onValueChange={(value) =>
                    setValue(
                      `claims.${index}.benefit_type`,
                      value ? (value as BenefitType) : null,
                    )
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select benefit type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={BenefitType.PERSONAL_INJURY_PROTECTION}>
                      Personal Injury Protection (PIP)
                    </SelectItem>
                    <SelectItem value={BenefitType.MEDICAL_PAYMENTS}>
                      Medical Payments
                    </SelectItem>
                    <SelectItem value={BenefitType.UNINSURED_MOTORIST}>
                      Uninsured Motorist
                    </SelectItem>
                    <SelectItem value={BenefitType.UNDER_INSURED_MOTORIST}>
                      Underinsured Motorist
                    </SelectItem>
                    <SelectItem value={BenefitType.BODILY_INJURY_LIABILITY}>
                      Bodily Injury Liability
                    </SelectItem>
                    <SelectItem value={BenefitType.PROPERTY_DAMAGE_LIABILITY}>
                      Property Damage Liability
                    </SelectItem>
                    <SelectItem value={BenefitType.COMPREHENSIVE_AUTO}>
                      Comprehensive Auto
                    </SelectItem>
                    <SelectItem value={BenefitType.COLLISION_AUTO}>
                      Collision Auto
                    </SelectItem>
                    <SelectItem value={BenefitType.OTHER}>Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        ))}

        {claimFields.length === 0 && (
          <div className="text-center py-4 text-gray-500">
            <p>No claims added yet.</p>
            <p className="text-sm">
              Click &quot;Add Claim&quot; to start adding claims.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
